<template>
  <GenericSideMenu
    position="right"
    :title="showHeadline"
    :self-contained="true"
    @close="$emit('close')"
  >
    <div class="quickBuyMultipleContent type-sm text-text-subtle mb-16" v-html="showContent" />
    <div v-if="products.length">
      <QuickBuyItem
        v-for="(product) in products"
        :key="product.partNo"
        :product="product"
        :stock-label="getStockLabel(product.mainVariant)"
        :category-code="categoryCode"
        @close="$emit('close')"
      />
    </div>

    <div v-if="extraText" class="flex p-16 gap-8 bg-bg-attention type-headline-sm mt-24 items-start">
      <img src="/icons/info.svg" class="w-20 relative top-1">
      <div>{{ extraText }}</div>
    </div>
  </GenericSideMenu>
</template>

<script setup lang="ts">
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import useApiFetch from '~/composeables/useApiFetch';
import { CategoryDetails } from '~/constants/types/norce';
import useAlgoliaFetch from '~/composeables/useAlgoliaFetch';
import Product, { ProductModel } from '~/models/product';
import QuickBuyItem from '~/components/product/QuickBuyItem.vue';
import useStockStatus from '~/composeables/useStockStatus';
const { apiGet } = useApiFetch();
const { $t } = useNuxtApp();

const { getExactVariantByPartNo, lastError,
  lastErrorCode } = useAlgoliaFetch();
const { getStockLabel } = useStockStatus();

interface Props {
  partNos?: string[]; // loads from algolia, either partNos or productsIn must be set
  productsIn?: ProductModel[]; // If product data is already loaded
  headline?: string;
  content?: string;
  categoryCode?: string | null; // loads info about a category from a norce, replaces headline and content
}

const props = withDefaults(defineProps<Props>(), {
  partNos: () => [] as string[],
  productsIn: () => [] as ProductModel[],
  headline: '',
  content: '',
  categoryCode: null,
});
defineEmits<{
  (e: 'close'): void;
}>();

const showHeadline = ref(props.headline);
const showContent = ref(props.content);
const extraText = $t(`multibuy.${props.categoryCode}.needMore`) !== `multibuy.${props.categoryCode}.needMore`
  ? $t(`multibuy.${props.categoryCode}.needMore`) : null;

const products = ref<ProductModel[]>(props.productsIn?.length ? props.productsIn : []);

onMounted(()=> {
  if (props.categoryCode) {
    setTimeout(()=> {
      loadCategoryInfo();
    }, 100);
  }
  if (props.partNos.length) {
    setTimeout(()=> {
      loadProducts();
    }, 100);
  }
});

const loadCategoryInfo = async() => {
  const res = await apiGet<CategoryDetails[]>(
    `/applications/categories?code=${props.categoryCode}`
  );
  if (res) {
    const thisRes = res.find(f => f.code === props.categoryCode);
    if (thisRes) {
      const h1Content = thisRes.description.match(/<h1>(.*?)<\/h1>/)?.[1];
      showHeadline.value = h1Content && h1Content !== '' ? h1Content : thisRes.name;
      showContent.value = thisRes.description.replace(/<h1>.*?<\/h1>/, '');
    }
  }
};

const loadProducts = async() => {
  const res = await getExactVariantByPartNo(props.partNos);

  if (res) {
    products.value = res.hits.map(p => Product.create([p]));
  } else {
    console.log(lastError.value);
    console.log(lastErrorCode.value);
  }
};

</script>

<style scoped lang="postcss">

</style>
